import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { QueryClient } from '@tanstack/react-query'

/**
 * Options for {@link useAppMutation}.
 *
 * @typeParam TPayload - Variables passed to `mutate`.
 * @typeParam TData - Resolved value of `mutationFn` before any mapping.
 * @typeParam TExposedData - Type of the exposed `data` field. Inferred from
 *   `mapData`; defaults to `TData | null`.
 * @typeParam TMutateResult - Type `mutate` resolves to. Inferred from
 *   `mapResult`; defaults to `void`.
 */
export interface UseAppMutationOptions<
  TPayload,
  TData,
  TExposedData = TData | null,
  TMutateResult = void,
> {
  /**
   * Performs the mutation. Synchronous throws and rejections are normalized
   * to `Error` instances before reaching TanStack Query.
   */
  mutationFn: (payload: TPayload) => Promise<TData>
  /**
   * Runs after a successful mutation and is awaited before `mutate` resolves.
   * Receives the mutation result, the payload, and the ambient query client
   * for cache updates and invalidations.
   */
  onSuccess?: (data: TData, payload: TPayload, queryClient: QueryClient) => void | Promise<void>
  /**
   * Best-effort failure reconciliation, awaited while the mutation is pending.
   * A callback failure never replaces the original mutation error.
   */
  onError?: (error: Error, payload: TPayload, queryClient: QueryClient) => void | Promise<void>
  /**
   * Maps the raw mutation state to the exposed `data` field. Defaults to
   * `error ? null : (data ?? null)`. `error` here is the normalized error,
   * so it is always an `Error` instance when set.
   */
  mapData?: (data: TData | undefined, error: Error | null) => TExposedData
  /**
   * Maps the settled mutation to the value `mutate` resolves with. Receives
   * mutation data on success and `null` after a mutation or mapper failure.
   * May be async. When omitted, `mutate` resolves to `void`.
   */
  mapResult?: (data: TData | null, payload: TPayload) => TMutateResult | Promise<TMutateResult>
}

export interface UseAppMutationResult<TPayload, TExposedData, TMutateResult> {
  /**
   * Runs the mutation. Never rejects: failures are exposed through the
   * `error` field instead, so callers read `error` state rather than
   * wrapping calls in try/catch.
   */
  mutate: (payload: TPayload) => Promise<TMutateResult>
  isPending: boolean
  /** `null` while `error` is set. */
  data: TExposedData
  /** Normalized to an `Error` instance; `null` after a successful run. */
  error: Error | null
}

/**
 * Shared factory for the app's TanStack Query mutation hooks.
 *
 * Convention: mutation hooks expose failures through the `error` field and
 * never reject from `mutate`. Callers render `error`/`isPending` state
 * instead of awaiting outcomes, so `mutate` always resolves. The factory
 * normalizes anything thrown by `mutationFn` (or reported through the
 * mutation's error state) to an `Error` instance.
 *
 * Feature hooks wrap this factory, keep their own exported return-type
 * interface, and use `onSuccess` for cache invalidations and store syncs.
 * Hooks whose `mutate` resolves to a value supply `mapResult`; hooks that
 * expose a transformed `data` field supply `mapData`.
 */
export function useAppMutation<TPayload, TData, TExposedData = TData | null, TMutateResult = void>(
  options: UseAppMutationOptions<TPayload, TData, TExposedData, TMutateResult>
): UseAppMutationResult<TPayload, TExposedData, TMutateResult> {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, data, error } = useMutation({
    mutationFn: async (payload: TPayload) => {
      try {
        return await options.mutationFn(payload)
      } catch (err) {
        throw err instanceof Error ? err : new Error(String(err))
      }
    },
    retry: false,
    onSuccess: (result, payload) => options.onSuccess?.(result, payload, queryClient),
    onError: async (error, payload) => {
      try {
        await options.onError?.(
          error instanceof Error ? error : new Error(String(error)),
          payload,
          queryClient
        )
      } catch {
        // Reconciliation must preserve the original error and never-reject contract.
      }
    },
  })

  const normalizedError = error ? (error instanceof Error ? error : new Error(String(error))) : null

  return {
    mutate: async (payload: TPayload): Promise<TMutateResult> => {
      try {
        const result = await mutateAsync(payload)
        return options.mapResult
          ? await options.mapResult(result, payload)
          : (undefined as TMutateResult)
      } catch {
        if (options.mapResult) {
          try {
            return await options.mapResult(null, payload)
          } catch {
            // Mapping failures must not break the app-wide never-reject contract.
          }
        }
        return undefined as TMutateResult
      }
    },
    isPending,
    // TExposedData is only inferred from `mapData`, so without `mapData` it
    // stays the `TData | null` default this expression produces.
    data: options.mapData
      ? options.mapData(data, normalizedError)
      : ((error ? null : (data ?? null)) as TExposedData),
    error: normalizedError,
  }
}
